import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [tier, setTier] = useState(null);

  const poll = useCallback(async (tries) => {
    if (!sessionId) { setState("error"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setTier(data.tier);
        setState("paid");
        refresh && refresh();
        return;
      }
      if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
    } catch {
      setState("error");
      return;
    }
    if (tries >= MAX_TRIES) { setState("pending"); return; }
    setTimeout(() => poll(tries + 1), 2000);
  }, [sessionId, refresh]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <Shell>
      <div className="max-w-md mx-auto text-center py-20" data-testid="payment-success">
        {state === "checking" && (
          <>
            <Loader2 size={36} className="animate-spin text-primary mx-auto mb-5" />
            <h1 className="font-serif text-3xl mb-2">Confirming your payment…</h1>
            <p className="text-muted-foreground">One moment while we activate your membership.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={40} className="text-primary mx-auto mb-5" />
            <h1 className="font-serif text-4xl mb-2" data-testid="payment-paid">You're upgraded!</h1>
            <p className="text-muted-foreground mb-8">
              Your {tier ? `${tier[0].toUpperCase()}${tier.slice(1)}` : "new"} membership is active. Enjoy the deeper features.
            </p>
            <Link to="/dashboard" className="inline-block py-3 px-8 rounded-full bg-primary text-[#0B0B12] font-medium text-sm">
              Back to your blueprint
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={36} className="text-primary mx-auto mb-5" />
            <h1 className="font-serif text-3xl mb-2">Almost there</h1>
            <p className="text-muted-foreground mb-8">Your payment is processing. Your membership will activate shortly.</p>
            <Link to="/dashboard" className="text-primary underline">Go to dashboard</Link>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle size={36} className="text-primary mx-auto mb-5" />
            <h1 className="font-serif text-3xl mb-2">Couldn't confirm payment</h1>
            <p className="text-muted-foreground mb-8">If you were charged, your membership will activate soon. Otherwise please try again.</p>
            <Link to="/pricing" className="text-primary underline">Back to membership</Link>
          </>
        )}
      </div>
    </Shell>
  );
}
